import { v4 } from 'uuid';
import { AGENT_SCENE_SETTINGS_CONFIG, DEFAULT_SCENE_ID } from '@/app/constants';
import type {
  AgentMediaItem,
  AgentReferenceContent,
  AgentSceneType,
  AgentSettings,
  AgentVoiceOption,
  TextPromptWithInputItem,
} from '@/app/types';
import type {
  ChatMessageItem,
  ChatMessageItemContent,
  PostChatMessage,
  RawRespChatMessage,
  ViduContentType,
} from '../_types';
import { ASPECT_RATIO_OPTIONS, RESOLUTION_OPTIONS } from '../constants';

/**
 * 获取场景的默认配置
 * @param sceneType - 场景类型
 * @returns 场景的默认配置，包含 duration、resolution 和 ratio 的默认值（取数组第一个元素）
 */
export function getDefaultSceneSettings(sceneType: AgentSceneType): AgentSettings {
  const config = AGENT_SCENE_SETTINGS_CONFIG[sceneType];

  return {
    duration: config?.duration?.[0],
    resolution: config?.resolution?.[0],
    ratio: config?.ratio?.[0],
  };
}

/**
 * 根据分辨率值获取对应的显示文案
 * @param resolution - 分辨率值（如 'Resolution_720p'）
 * @returns 对应的显示文案（如 '720p'），找不到则返回空字符串
 */
export function getResolutionLabel(resolution: AgentSettings['resolution']): string {
  const option = RESOLUTION_OPTIONS.find((opt) => opt.value === resolution);
  return option?.label || '';
}

/**
 * 根据宽高比值获取对应的显示文案
 * @param ratio - 宽高比值（如 'Ratio_16_9'）
 * @returns 对应的显示文案（如 '16:9'），找不到则返回空字符串
 */
export function getAspectRatioLabel(ratio: AgentSettings['ratio']): string {
  const option = ASPECT_RATIO_OPTIONS.find((opt) => opt.value === ratio);
  return option?.label || '';
}

/**
 * 获取分辨率选项列表（包含 label 和 value）
 * @param values - 要筛选的分辨率值数组
 * @returns 筛选后的分辨率选项列表
 */
export function getResolutionOptions(values: AgentSettings['resolution'][]) {
  return RESOLUTION_OPTIONS.filter((opt) => values.includes(opt.value));
}

/**
 * 获取宽高比选项列表（包含 label 和 value）
 * @param values - 要筛选的宽高比值数组
 * @returns 筛选后的宽高比选项列表
 */
export function getAspectRatioOptions(values: AgentSettings['ratio'][]) {
  return ASPECT_RATIO_OPTIONS.filter((opt) => values.includes(opt.value));
}

/**
 * 转换上传的媒体列表为后端格式
 */
function transformUploadList(uploadList: AgentMediaItem[]) {
  return uploadList.map((item) => ({
    id: item.id,
    type: item.type,
    video: item.type === 'video' ? { url: item.url, cover_url: item.cover_url } : undefined,
    image: item.type === 'image' ? { url: item.cover_url || item.url } : undefined,
    tags: ['upload' as const],
  }));
}

const transformAgentModelDescription = (agentModelDescription: string) => {
  return `[${agentModelDescription}]`;
};

/**
 * 转换用户输入为后端格式
 */
function transformInput(input: TextPromptWithInputItem) {
  return input.map((item) => {
    // 处理 InputItem 类型（@引用的内容）
    if (item.type === 'inputItem') {
      return {
        id: item.inputItem.id,
        type: item.inputItem.type,
        image: item.inputItem.type === 'image' ? { url: item.inputItem.content } : undefined,
        video: item.inputItem.type === 'video' ? { url: item.inputItem.content } : undefined,
        script: item.inputItem.type === 'script' ? { text: item.inputItem.content } : undefined,
        tags: ['at' as const],
      };
    }

    if (item.type === 'agent-model') {
      return {
        type: 'text' as const,
        text: transformAgentModelDescription(item.agentModel.description),
      };
    }

    // 处理普通文本
    return {
      type: 'text' as const,
      text: item.type === 'text' ? item.text : '',
    };
  });
}

/**
 * 转换引用内容为后端格式
 */
function transformReference(reference: AgentReferenceContent | null | undefined) {
  if (!reference) return [];

  switch (reference.type) {
    case 'image':
      return (
        reference.images?.map((item) => ({
          id: reference.id,
          type: reference.type,
          image: {
            url: item.url || '',
          },
          tags: ['quote' as const],
        })) || []
      );
    case 'video':
      return (
        reference.videos?.map((item) => ({
          id: reference.id,
          type: reference.type,
          video: {
            url: item.url || '',
            cover_url: item.cover_url || '',
          },
          tags: ['quote' as const],
        })) || []
      );
    case 'script':
      return [
        {
          id: reference.id,
          type: reference.type,
          script: { text: reference.script?.text || '' },
          tags: ['quote' as const],
        },
      ];
    default:
      return [];
  }
}

/**
 * 创建设置内容
 */
function createSettingsContent({
  sceneId,
  voiceId,
  settings,
}: {
  sceneId: AgentSceneType;
  voiceId: string;
  settings: AgentSettings;
}) {
  return {
    type: 'settings' as const,
    settings: {
      scene: sceneId,
      duration: settings.duration,
      resolution: settings.resolution,
      ratio: settings.ratio,
      net_mode: settings.net_mode,
      voice_id: voiceId,
    },
  };
}

/**
 * 将用户输入转换为后端请求的消息格式
 * @param params - 包含场景 ID、设置、输入、上传列表和引用的参数对象
 * @returns 转换后的后端消息格式
 */
export const transformInput2PostMessage = ({
  sceneId,
  settings,
  input,
  uploadList,
  reference,
  voiceId = 'auto',
}: {
  voiceId?: string;
  sceneId: AgentSceneType;
  settings: AgentSettings;
  input: TextPromptWithInputItem;
  uploadList: AgentMediaItem[];
  reference?: AgentReferenceContent | null;
}): PostChatMessage => {
  // 转换各部分内容
  const transformedReference = transformReference(reference);
  const transformedUploadList = transformUploadList(uploadList);
  const transformedInput = transformInput(input);
  const settingsContent = createSettingsContent({ sceneId, voiceId, settings });

  // 组合所有内容：引用 -> 上传 -> 输入 -> 设置
  return {
    id: `${Date.now()}`,
    role: 'user',
    contents: [...transformedReference, ...transformedUploadList, ...transformedInput, settingsContent],
  };
};

/**
 * 创建媒体项
 * @param item - 内容项
 * @returns 媒体项对象
 */
function createMediaItem(item: ViduContentType): AgentMediaItem {
  return {
    id: item.id ?? '',
    type: item.type as 'image' | 'video',
    ...(item.type === 'image'
      ? { url: item.image?.url ?? '' }
      : { url: item.video?.url ?? '', cover_url: item.video?.cover_url ?? '' }),
  };
}

/**
 * 创建内容项
 * @param item - 内容项
 * @returns 聊天消息内容项
 */
function createContentItem(item: ViduContentType): ChatMessageItemContent {
  return {
    id: item.id && item.id !== '0' ? item.id : v4(),
    extraTag: item.tags?.includes('at') ? 'at' : undefined,
    type: item.type as ChatMessageItemContent['type'],
    text: item.text?.trim() ? [item.text.trim()] : [],
    image: item.image?.url ? [{ url: item.image.url }] : [],
    video: item.video?.url ? [{ url: item.video.url, cover_url: item.video.cover_url }] : [],
    script: item.script?.text ? [{ text: item.script.text }] : [],
    link: [],
    audio: [],
    plan: [],
    query_card: item.query_card,
  };
}

/**
 * 处理设置项
 * @param item - 内容项
 * @returns 设置对象或 undefined
 */
function processSettings(
  item: ViduContentType,
): (AgentSettings & { scene: AgentSceneType } & { voice?: AgentVoiceOption }) | undefined {
  const { scene, duration, resolution, ratio, net_mode, voice_profile } = item.settings || {};
  return {
    scene: scene === 'unspecified' ? DEFAULT_SCENE_ID : (scene ?? DEFAULT_SCENE_ID),
    voice: voice_profile
      ? {
          ...voice_profile,
          language:
            !voice_profile?.language || voice_profile?.language === 'unspecified' ? 'en' : voice_profile?.language,
          img: voice_profile?.avatar_url || '',
          url: voice_profile?.sample_url || '',
        }
      : undefined,
    duration: duration,
    resolution: resolution === 'UNSPECIFIED' ? undefined : resolution,
    ratio: ratio === 'UNSPECIFIED' ? undefined : ratio,
    net_mode: net_mode === 'unspecified' ? undefined : net_mode,
  };
}

/**
 * 处理引用内容
 * @param item - 内容项
 * @param currentReference - 当前的引用对象
 * @returns 更新后的引用对象或 undefined
 */
function processReference(
  item: ViduContentType,
  currentReference: AgentReferenceContent | undefined,
): AgentReferenceContent | undefined {
  const refType = item.type as AgentReferenceContent['type'];

  // 如果已有引用，追加内容
  if (currentReference?.id) {
    if (refType === 'image') {
      currentReference.images?.push({ url: item.image?.url || '' });
    } else if (refType === 'video') {
      currentReference.videos?.push({ url: item.video?.url || '', cover_url: item.video?.cover_url || '' });
    } else if (refType === 'script') {
      currentReference.script = { text: item.script?.text || '' };
    }
    return currentReference;
  }

  // 创建新的引用
  return {
    id: item.id ?? '',
    type: refType,
    images: refType === 'image' ? [{ url: item.image?.url || '' }] : undefined,
    videos: refType === 'video' ? [{ url: item.video?.url || '', cover_url: item.video?.cover_url || '' }] : undefined,
    script: refType === 'script' ? { text: item.script?.text || '' } : undefined,
  };
}

/**
 * 判断是否为上传媒体项
 * @param item - 内容项
 * @returns 是否为上传媒体
 */
function isUploadMedia(item: ViduContentType): boolean {
  return Boolean(item.tags?.includes('upload') && item.type !== 'text' && item.type !== 'script');
}

/**
 * 将后端响应的消息转换为前端使用的消息格式
 * @param rawRespChatMessage - 后端原始消息
 * @returns 包含消息和可选设置的对象
 */
export const transformMessage2MessageItem = (
  rawRespChatMessage: RawRespChatMessage,
): {
  message: ChatMessageItem;
  settings?: AgentSettings & { scene: AgentSceneType } & { voice?: AgentVoiceOption };
} => {
  const contents: ChatMessageItemContent[] = [];
  const uploadMediaList: AgentMediaItem[] = [];
  let settings: (AgentSettings & { scene: AgentSceneType } & { voice?: AgentVoiceOption }) | undefined;
  let reference: AgentReferenceContent | undefined;

  // 遍历并处理所有内容项
  rawRespChatMessage.contents?.forEach((item) => {
    // 跳过无效类型
    if (!item.type || item.type === 'unspecified') {
      return;
    }

    // 1. 处理设置
    if (item.type === 'settings') {
      const processedSettings = processSettings(item);
      if (processedSettings) {
        settings = processedSettings;
      }
      return;
    }

    // 2. 处理引用
    if (item.tags?.includes('quote') && item.type !== 'text') {
      reference = processReference(item, reference);
      return;
    }

    // 3. 处理上传媒体
    if (isUploadMedia(item)) {
      uploadMediaList.push(createMediaItem(item));
      return;
    }

    if (item.type === 'cost' && item.cost?.cost_type && item.cost?.cost_type !== 'unspecified') {
      contents.push({
        id: item.id || v4(),
        type: 'cost',
        cost: {
          cost_credits: item.cost?.cost_credits ?? 0,
          original_credits: item.cost?.original_credits ?? 0,
          cost_type: item.cost?.cost_type,
        },
      });
      return;
    }

    // 4. 处理普通内容
    contents.push(createContentItem(item));
  });

  // 构建最终消息对象
  return {
    message: {
      id: rawRespChatMessage.id ?? `${Date.now()}`,
      created_at: rawRespChatMessage.created_at ?? new Date().toISOString(),
      role: rawRespChatMessage.role === 'unspecified' ? 'assistant' : (rawRespChatMessage.role ?? 'assistant'),
      status: 'success',
      contents,
      ref: reference,
      uploadMediaList,
    },
    settings,
  };
};

/**
 * 从图片 URL 下载图片并转换为 File 对象
 */
export const downloadImageAsFile = async (url: string, filename: string): Promise<File | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();
    const file = new File([blob], filename, { type: blob.type });
    return file;
  } catch (error) {
    console.error('Error downloading image:', error);
    return null;
  }
};

export const roundToNearestTarget = (value: number): 15 | 30 => {
  const TARGET_15 = 15;
  const TARGET_30 = 30;

  const distance15 = Math.abs(value - TARGET_15);
  const distance30 = Math.abs(value - TARGET_30);

  return distance15 < distance30 ? TARGET_15 : TARGET_30;
};

/**
 * 将 agent 视频的 ratio 枚举转换为宽高数值（用于 MediaWithWatermark 计算水印位置）
 * @param ratio agent 视频比例枚举，如 'Ratio_16_9'
 * @returns [width, height]，无法识别时默认 16:9
 */
export const ratioToWidthHeight = (ratio?: AgentSettings['ratio'] | 'UNSPECIFIED'): [number, number] => {
  switch (ratio) {
    case 'Ratio_16_9':
      return [16, 9];
    case 'Ratio_9_16':
      return [9, 16];
    case 'Ratio_3_4':
      return [3, 4];
    case 'Ratio_1_1':
      return [1, 1];
    case 'Ratio_4_3':
      return [4, 3];
    case 'Ratio_21_9':
      return [21, 9];
    case 'Ratio_other':
      // other 表示自定义比例，应从 video_width/height 获取真实值，此处 16:9 仅作兜底
      return [16, 9];
    default:
      return [16, 9];
  }
};
